import TaskRow from './TaskRow';
import { Fragment, useMemo, useState } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableFeatures,
} from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';
import useItemTableStyles from '@modules/items/hooks/useItemTableStyles.hook';
import type { TResolvedItemsMap } from '@modules/items/types/items.types';
import { usePersistedColumnSizing } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { TASK_COLUMN_LABELS, TASK_COLUMN_WIDTHS, TASKS_TABLE_ID } from '../constants';
import { type ETaskStatus, type TTaskListItem, type TTaskTableSection } from '../types';

type TColumnId = 'number' | 'title' | 'status' | 'assignee' | 'dueDate' | 'priority';
const metadataColumns = ['assignee', 'dueDate', 'priority'] as const;

// Match Projects: auto-fit must not overwrite user-resized widths.
const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    number: { defaultWidth: 64, minWidth: 48 },
    title: { defaultWidth: 280, minWidth: 160 },
    status: { defaultWidth: TASK_COLUMN_WIDTHS.status, minWidth: 128 },
    assignee: { defaultWidth: TASK_COLUMN_WIDTHS.assignee, minWidth: 100 },
    dueDate: { defaultWidth: TASK_COLUMN_WIDTHS.dueDate, minWidth: 110 },
    priority: { defaultWidth: TASK_COLUMN_WIDTHS.priority, minWidth: 90 },
  },
};
const columnIds: TColumnId[] = ['number', 'title', 'status', ...metadataColumns];

const TasksTable = ({
  tasks,
  resolvedItems,
  projectId,
  onStatusChange,
  statusPending,
  onArchive,
  canReorder,
  reorderDisabledReason,
  revision,
  onReorder,
  sections,
}: {
  canReorder: boolean;
  reorderDisabledReason: string;
  revision: number;
  onReorder: (sourceId: string, targetId: string) => void;
  tasks: TTaskListItem[];
  resolvedItems: TResolvedItemsMap;
  projectId: string;
  onStatusChange: (task: TTaskListItem, status: ETaskStatus) => void;
  statusPending: boolean;
  onArchive: (task: TTaskListItem) => void;
  /** Groups the rows under collapsible headers, numbered per section. */
  sections?: TTaskTableSection[];
}) => {
  const columns = useMemo(() => columnIds.map((columnId) => createTableColumn<TTaskListItem>({ columnId })), []);
  const { columnSizing_unstable, getRows } = useTableFeatures({ columns, items: tasks, getRowId: (task) => task._id }, [
    usePersistedColumnSizing(TASKS_TABLE_ID, columnSizingParams),
  ]);
  const rows = getRows();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const tableClasses = useItemTableStyles();
  const renderRow = (task: TTaskListItem, index: number, section?: string) => (
    <TaskRow
      key={task._id}
      task={task}
      resolvedItems={resolvedItems}
      number={index + 1}
      projectId={projectId}
      getCellProps={columnSizing_unstable.getTableCellProps}
      onStatusChange={onStatusChange}
      statusPending={statusPending}
      onArchive={onArchive}
      canReorder={canReorder}
      disabledReason={reorderDisabledReason}
      revision={revision}
      onReorder={onReorder}
      section={section}
    />
  );
  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('number')}>
              <Body1Strong>No.</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('title')}>
              <Body1Strong>{TASK_COLUMN_LABELS.title}</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('status')}>
              <Body1Strong>{TASK_COLUMN_LABELS.status}</Body1Strong>
            </TableHeaderCell>
            {metadataColumns.map((column) => (
              <TableHeaderCell key={column} {...columnSizing_unstable.getTableHeaderCellProps(column)}>
                <Body1Strong>{TASK_COLUMN_LABELS[column]}</Body1Strong>
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {sections
            ? sections.map((section) => {
                const isExpanded = expanded[section.id] ?? section.defaultExpanded;
                return (
                  <Fragment key={section.id}>
                    <TableRow className="!bg-neutralBg-3">
                      <TableCell colSpan={columnIds.length}>
                        <div className="flex items-center gap-2">
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                            onClick={() => setExpanded({ ...expanded, [section.id]: !isExpanded })}
                          />
                          {section.header}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded &&
                      (section.tasks.length ? (
                        section.tasks.map((task, index) => renderRow(task, index, section.id))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={columnIds.length}>
                            <Body1 className="text-neutralFg-3">No {TERMINOLOGY.task.lowerPlural}.</Body1>
                          </TableCell>
                        </TableRow>
                      ))}
                  </Fragment>
                );
              })
            : rows.map(({ item: task }, index) => renderRow(task, index))}
        </TableBody>
      </Table>
    </Card>
  );
};
export default TasksTable;
